import { FC, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import DeceasedTable from './table';
import {
  createSectionByIdSelector,
} from '../../../../redux/pages/selectors';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { RSOverviewPageModel } from '../../../../types/server-data/ClientConfig';
import type { RiskOverviewMonitorFundRow } from '../../../../api/rs-hg/risk/riskOverview.types';
import {
  ReportsListByFund,
  useReportsListQuery,
  useRiskOverviewMonitorQuery,
} from '../../../../api/rs-hg/risk/hooks/queries';
import { sortFundNames } from '../../../../utilities/customSorting';

type DeceasedPageData = {
  overview: RSOverviewPageModel<RiskOverviewMonitorFundRow[]>;
  reports: ReportsListByFund;
};

const Deceased: FC<GeneralOverviewPageRenderedComponentProps> = (props) => {
  const sectionDetails = useSelector(createSectionByIdSelector(props.section));
  const fundTypes = sectionDetails?.fundTypes;
  const selectedRaptorPlatformId = useSelector(raptorPlatformIdSelector);

  const reportsListQuery = useReportsListQuery({
    fundTypes,
    raptorPlatformId: selectedRaptorPlatformId,
    status: 'deceased',
  });

  const deceasedOverviewQuery = useRiskOverviewMonitorQuery({
    positionDate: props.positionDate,
    fundTypes,
    raptorPlatformId: selectedRaptorPlatformId,
    deceased: true,
  });

  const pageData = useMemo((): DeceasedPageData | null => {
    if (!deceasedOverviewQuery.data?.data) {
      return null;
    }

    const overviewRows = [...deceasedOverviewQuery.data.data].sort((a, b) =>
      sortFundNames(a.fund_name || '', b.fund_name || ''),
    );

    return {
      overview: {
        ...deceasedOverviewQuery.data,
        data: overviewRows,
      },
      reports: reportsListQuery.data ?? {},
    };
  }, [deceasedOverviewQuery.data, reportsListQuery.data]);


  return (
    <QueryBoundary<DeceasedPageData>
      isLoading={deceasedOverviewQuery.isLoading}
      isFetching={deceasedOverviewQuery.isFetching}
      isError={!!deceasedOverviewQuery.error}
      error={deceasedOverviewQuery.error}
      data={pageData}
      loadingMessages={['Loading Deceased Data...']}
      errorMessage="No Data Available."
      noDataMessage="No Data Available."
    >
      {({ overview, reports }) => (
        <DeceasedTable overviewData={overview} reportsList={reports} />
      )}
    </QueryBoundary>
  );
};

export default Deceased;
